import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { apiFetch, getApiHost } from '../utils/api';

interface LivingDangerouslyToken {
  mint: string;
  symbol: string | null;
  name: string | null;
  classifiedAt: string;
  criticalRisks: string[];
  warnings: string[];
  flag?: string;
  multiplier: number;
  entryPriceSol: number;
  currentPriceSol: number;
  liquidityUsd: number;
  lastTradeUnix: number;
  tokenCreatedAt?: string;
}

interface ApiResponse {
  level: 'OK' | 'RISKY' | 'DANGEROUS';
  count: number;
  sampledCandidates: number;
  pricedCandidates: number;
  aliveCandidates: number;
  tokens: LivingDangerouslyToken[];
}

interface Props {
  level?: 'OK' | 'RISKY' | 'DANGEROUS';
  title?: string;
}

const fmtAge = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  const days = ms / 86400_000;
  if (days < 1) return `${(ms / 3600_000).toFixed(1)}h`;
  if (days < 30) return `${days.toFixed(1)}d`;
  return `${(days / 30).toFixed(1)}mo`;
};

const fmtUsd = (n?: number) => {
  if (n == null || !isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};

const fmtMultiplier = (n: number) => {
  if (n >= 10) return `${n.toFixed(1)}×`;
  return `${n.toFixed(2)}×`;
};

const LivingDangerouslyPage: React.FC<Props> = ({ level = 'DANGEROUS', title = 'living_dangerously' }) => {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    (async () => {
      try {
        const res = await apiFetch(`${getApiHost()}/api/internal/living-dangerously?level=${level}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: ApiResponse = await res.json();
        if (!cancelled) setData(json);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [level]);

  return (
    <div className="min-h-screen bg-crypto-dark text-white">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-gray-400 text-sm mt-1">
            Tokens we classified {level} 3–30 days ago that are still trading <em>and</em> currently above their classification-time price.
            Sorted most-recent classification first. Slow load (Birdeye fan-out).
          </p>
        </div>

        {loading && <div className="text-gray-400 text-sm py-12 text-center">Loading… (typically 5–15s)</div>}
        {error && <div className="text-red-400 text-sm py-12 text-center">Error: {error}</div>}

        {data && (
          <>
            <div className="text-xs text-gray-500 mb-3">
              {data.count} up · {data.aliveCandidates} alive · {data.pricedCandidates} priced · {data.sampledCandidates} {data.level} classifications sampled (3–30d).
              Alive ≠ priced: low-liquidity tokens with no trades in 1h are filtered (post-event liquidity rugs).
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-gray-400 text-xs uppercase border-b border-gray-700">
                  <tr>
                    <th className="text-left py-2 pr-3">Token</th>
                    <th className="text-right py-2 px-3">Multiplier</th>
                    <th className="text-right py-2 px-3">Liquidity</th>
                    <th className="text-left py-2 px-3">Flag</th>
                    <th className="text-left py-2 px-3">Critical risks</th>
                    <th className="text-right py-2 pl-3">Classified</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tokens.map(t => (
                    <tr key={t.mint} className="border-b border-gray-800 hover:bg-gray-800/40">
                      <td className="py-3 pr-3">
                        <Link to={`/token/${t.mint}`} className="text-crypto-accent hover:underline">
                          {t.symbol || t.mint.slice(0, 6)}
                        </Link>
                        {t.name && <div className="text-gray-500 text-xs truncate max-w-xs">{t.name}</div>}
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-green-400">{fmtMultiplier(t.multiplier)}</td>
                      <td className="py-3 px-3 text-right font-mono">{fmtUsd(t.liquidityUsd)}</td>
                      <td className="py-3 px-3 text-yellow-400 text-xs">{t.flag || '—'}</td>
                      <td className="py-3 px-3 text-xs text-gray-300 max-w-md">
                        {(t.criticalRisks || []).slice(0, 2).join(' · ') || (t.warnings || []).slice(0, 2).join(' · ') || '—'}
                      </td>
                      <td className="py-3 pl-3 text-right text-gray-400 text-xs">{fmtAge(t.classifiedAt)} ago</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.tokens.length === 0 && (
                <div className="text-gray-500 text-sm py-12 text-center">No DANGEROUS tokens currently above their classification price.</div>
              )}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default LivingDangerouslyPage;
